<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  options: { type: Array, required: true },
  modelValue: { type: String, required: true },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);

const STATUS_CLASSES = {
  open: 'bg-n-teal-3 text-n-teal-11',
  abandoned: 'bg-n-slate-3 text-n-slate-11',
  converted: 'bg-n-blue-3 text-n-blue-11',
};

const selected = computed(() =>
  props.options.find(o => o.value === props.modelValue)
);

const handleSelect = value => {
  emit('update:modelValue', value);
  isOpen.value = false;
};
</script>

<template>
  <div v-on-clickaway="() => (isOpen = false)" class="relative">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-n-slate-12 transition-all duration-100 ease-out hover:bg-n-alpha-2 active:scale-[0.97]"
      @click="isOpen = !isOpen"
    >
      <span
        v-if="selected?.status"
        class="rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none"
        :class="STATUS_CLASSES[selected.status]"
      >
        {{ selected.statusLabel }}
      </span>
      <span class="truncate max-w-[120px]">
        {{ selected?.cartId ? `#${selected.cartId}` : selected?.label }}
      </span>
      <span class="i-lucide-chevron-down flex-shrink-0" />
    </button>

    <div
      v-if="isOpen"
      class="absolute top-full mt-0.5 ltr:left-0 rtl:right-0 z-40 min-w-[190px] flex flex-col p-2 bg-n-alpha-3 backdrop-blur-[100px] rounded-lg shadow-lg border border-n-strong dark:border-n-strong"
    >
      <button
        v-for="opt in options"
        :key="opt.value"
        type="button"
        class="inline-flex items-center gap-2 w-full px-2.5 h-7 text-xs rounded-lg text-n-slate-12 hover:bg-n-alpha-2 outline outline-1 outline-transparent transition-all duration-100 ease-out active:scale-[0.97] justify-start"
        :class="{ 'bg-n-alpha-2': opt.value === modelValue }"
        @click="handleSelect(opt.value)"
      >
        <span
          v-if="opt.status"
          class="rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none flex-shrink-0"
          :class="STATUS_CLASSES[opt.status]"
        >
          {{ opt.statusLabel }}
        </span>
        <span v-else class="i-lucide-plus flex-shrink-0" />
        <span class="truncate flex-grow text-left">{{ opt.label }}</span>
        <span v-if="opt.value === modelValue" class="i-lucide-check flex-shrink-0 ml-auto" />
      </button>
    </div>
  </div>
</template>
